'use client';

import Image from 'next/image';
import { DotBlockEditor } from '@/components/shared/decodeTextBlock';
import customRenderers from '@/components/block-editor';
import LinkNoPrefetch from '@/components/link-noprefetch';
import {
    IconExternalLink,
    IconMail,
    IconMessageCircle,
    IconMapPin,
    IconUsers
} from '@tabler/icons-react';
import { CollectionCard } from '@/components/content-types/collection-type/components/collectionCard';

function ActionButton({ href, children, variant = 'primary', external = false }) {
    if (!href) return null;

    const base =
        'inline-flex items-center gap-2 rounded-lg border-2 border-solid px-5 py-2.5 text-sm font-medium transition-colors duration-300 no-underline';
    const styles = {
        primary: 'border-blue-700 bg-blue-700 text-white hover:bg-white hover:text-blue-700',
        outlined: 'border-blue-700 bg-transparent text-blue-700 hover:bg-blue-700 hover:text-white'
    };
    const className = `${base} ${styles[variant] || styles.outlined}`;

    if (external) {
        return (
            <a href={href} className={className} target="_blank" rel="noopener noreferrer">
                {children}
            </a>
        );
    }

    return (
        <LinkNoPrefetch href={href} className={className}>
            {children}
        </LinkNoPrefetch>
    );
}

function SidebarCard({ title, children }) {
    return (
        <div className="rounded-lg border border-gray-200 p-6">
            <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                {title}
            </h3>
            {children}
        </div>
    );
}

function ResourceLink({ href, icon: Icon, label }) {
    if (!href) return null;

    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 py-1.5 text-sm text-blue-700 hover:text-blue-800 hover:underline">
            <Icon size={16} />
            {label}
        </a>
    );
}

export default function PartnerDetail({ partner, relatedBlogs = [] }) {
    const {
        title,
        overview,
        website1,
        email,
        identifier,
        logo,
        type1,
        geography,
        forPartners,
        thumbnailAlt,
        caseStudyLink
    } = partner;

    const geographyList = Array.isArray(geography) ? geography.join(', ') : geography;

    const toAbsoluteUrl = (url) => {
        if (!url) return url;
        if (/^https?:\/\//i.test(url)) return url;
        return `https://${url}`;
    };

    const websiteUrl = toAbsoluteUrl(website1);
    const caseStudyUrl = toAbsoluteUrl(caseStudyLink);

    return (
        <>
            {/* Header */}
            <div className="background-crossline mb-10 p-8 pt-12 lg:p-16 lg:px-32">
                <div className="mx-auto flex max-w-2xl flex-col items-center text-center">
                    {logo ? (
                        <div className="mb-4 flex w-full items-center justify-center px-6">
                            <div className="relative h-24 w-full">
                                <Image
                                    src={`/dA/${identifier}/image`}
                                    alt={thumbnailAlt || title}
                                    fill
                                    sizes="200px"
                                    className="object-contain"
                                />
                            </div>
                        </div>
                    ) : (
                        <h1 className="text-4xl font-bold">{title}</h1>
                    )}

                    {logo && <h2 className="mt-2 text-2xl font-semibold text-gray-800">{title}</h2>}

                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <ActionButton href={websiteUrl} variant="primary" external>
                            <IconExternalLink size={18} />
                            Visit website
                        </ActionButton>
                        {email && (
                            <ActionButton href={`mailto:${email}`} variant="outlined" external>
                                <IconMail size={18} />
                                Contact partner
                            </ActionButton>
                        )}
                        {caseStudyUrl && (
                            <ActionButton href={caseStudyUrl} variant="outlined" external>
                                <IconUsers size={18} />
                                Case study
                            </ActionButton>
                        )}
                    </div>
                </div>
            </div>

            {/* Two-column layout */}
            <div className="container mb-24 grid grid-cols-6 gap-10">
                <div className="col-span-4">
                    {overview && (
                        <section className="mb-10">
                            <h2 className="mb-4 text-2xl font-bold">About</h2>
                            <div className="prose prose-sm max-w-none prose-h2:text-2xl prose-h3:text-xl">
                                <DotBlockEditor
                                    blocks={overview}
                                    customRenderers={customRenderers}
                                />
                            </div>
                        </section>
                    )}

                    {relatedBlogs.length > 0 && (
                        <section>
                            <h2 className="mb-6 text-2xl font-bold">Related Posts</h2>
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                {relatedBlogs.map((blog) => (
                                    <CollectionCard
                                        key={blog.identifier}
                                        contentlet={blog}
                                        contenttype="Blog"
                                        showCategory={true}
                                        showAuthor={true}
                                    />
                                ))}
                            </div>
                        </section>
                    )}
                </div>

                <aside className="col-span-2 flex flex-col gap-6">
                    <SidebarCard title="Details">
                        <dl className="space-y-3 text-sm">
                            {type1 && (
                                <div className="flex justify-between">
                                    <dt className="text-gray-500">Type</dt>
                                    <dd className="font-medium text-gray-900">
                                        {type1 === 'partner'
                                            ? 'Solution'
                                            : type1.charAt(0).toUpperCase() + type1.slice(1)}
                                    </dd>
                                </div>
                            )}
                            {geographyList && (
                                <div className="flex justify-between gap-4">
                                    <dt className="shrink-0 text-gray-500">Geography</dt>
                                    <dd className="text-right font-medium text-gray-900">
                                        {geographyList}
                                    </dd>
                                </div>
                            )}
                            {forPartners && (
                                <div className="flex justify-between">
                                    <dt className="text-gray-500">For partners</dt>
                                    <dd className="font-medium text-gray-900">{forPartners}</dd>
                                </div>
                            )}
                        </dl>
                    </SidebarCard>

                    {(website1 || email) && (
                        <SidebarCard title="Resources">
                            <div className="flex flex-col gap-1">
                                <ResourceLink
                                    href={websiteUrl}
                                    icon={IconExternalLink}
                                    label="Partner website"
                                />
                                {email && (
                                    <ResourceLink
                                        href={`mailto:${email}`}
                                        icon={IconMail}
                                        label={email}
                                    />
                                )}
                                {geographyList && (
                                    <div className="flex items-center gap-2 py-1.5 text-sm text-gray-600">
                                        <IconMapPin size={16} />
                                        {geographyList}
                                    </div>
                                )}
                            </div>
                        </SidebarCard>
                    )}

                    <SidebarCard title="Need help?">
                        <p className="mb-4 text-sm text-gray-600">
                            Have questions about this partner? Our team is here to help you get
                            started.
                        </p>
                        <ActionButton href="/contact-us" variant="primary">
                            <IconMessageCircle size={16} />
                            Talk to an expert
                        </ActionButton>
                    </SidebarCard>
                </aside>
            </div>
        </>
    );
}
